import { Link, router, useLocalSearchParams } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  CheckSquare2,
  Circle,
  CreditCard,
  Fingerprint,
  IdCard,
  LockKeyhole,
  Mail,
  Phone,
  ShieldCheck,
  Square,
  Sparkles,
  X,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { Input } from '@/src/components/Input';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  publicProfileQueryKey,
  useProfile,
} from '@/src/features/profile/use-profile';
import {
  confirmPhoneVerificationCode,
  createEmbeddedVerifiedAccountCheckoutSession,
  refreshVerifiedAccountStatus,
  requestVerifiedAccountRefund,
  sendPhoneVerificationCode,
  startIdentityVerification,
  startVerifiedAccountCheckout,
} from '@/src/features/verification/verification-service';
import {
  formatUsPhoneNumberDisplay,
  formatUsPhoneNumberInput,
  normalizeUsPhoneNumber,
} from '@/src/features/verification/phone-number';
import { PhoneVerificationCodeModal } from '@/src/features/verification/PhoneVerificationCodeModal';
import { VerifiedAccountEmbeddedCheckout } from '@/src/features/verification/VerifiedAccountEmbeddedCheckout';
import {
  parseVerifiedAccount,
  nextVerifiedAccountWizardStep,
  type VerifiedAccountWizardStep,
} from '@/src/features/verification/verification-status';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

const paymentReturnPollAttempts = 8;
const paymentReturnPollDelayMs = 1500;

const orderedSteps: {
  description: string;
  key: Exclude<VerifiedAccountWizardStep, 'complete'>;
  title: string;
}[] = [
  {
    description: '$10 plus applicable tax',
    key: 'payment',
    title: 'Pay processing fee',
  },
  {
    description: 'Email and cell phone',
    key: 'contact',
    title: 'Confirm contact info',
  },
  {
    description: 'Document and selfie',
    key: 'identity',
    title: 'Verify identity',
  },
];

type StepArtKind = 'complete' | 'contact' | 'identity' | 'payment';

type ChecklistItemProps = {
  complete?: boolean;
  text: string;
};

function statusLabel(complete: boolean, active: boolean) {
  if (complete) {
    return 'Complete';
  }

  return active ? 'Current step' : 'Locked';
}

function ChecklistItem({ complete = false, text }: ChecklistItemProps) {
  return (
    <View style={styles.checklistItem}>
      <CheckCircle2
        color={complete ? colors.success : colors.primary}
        size={18}
        strokeWidth={2.4}
      />
      <Text style={styles.checklistText}>{text}</Text>
    </View>
  );
}

function wait(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function StepArt({ kind }: { kind: StepArtKind }) {
  const Icon =
    kind === 'payment'
      ? CreditCard
      : kind === 'contact'
        ? Phone
        : kind === 'identity'
          ? Fingerprint
          : ShieldCheck;
  const AccentIcon =
    kind === 'payment'
      ? LockKeyhole
      : kind === 'contact'
        ? Mail
        : kind === 'identity'
          ? IdCard
          : CheckCircle2;

  return (
    <View style={styles.stepArt} pointerEvents="none">
      <View style={styles.stepArtGlow} />
      <View style={[styles.stepArtOrb, styles.stepArtOrbPrimary]}>
        <Icon color={colors.surface} size={44} strokeWidth={2.4} />
      </View>
      <View style={[styles.stepArtOrb, styles.stepArtOrbAccent]}>
        <AccentIcon color={colors.primary} size={24} strokeWidth={2.4} />
      </View>
      <View style={styles.stepArtSpark}>
        <Sparkles color={colors.warmAccent} size={18} strokeWidth={2.5} />
      </View>
    </View>
  );
}

export default function VerifyAccountScreen() {
  const { refreshCurrentUser, sendVerificationEmail, user } = useAuth();
  const { showError, showSuccess } = useToast();
  const queryClient = useQueryClient();
  const params = useLocalSearchParams<{
    identity?: string;
    verificationPayment?: string;
  }>();
  const profileQuery = useProfile(user?.uid);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [phoneVerificationId, setPhoneVerificationId] = useState<string | null>(
    null,
  );
  const [phoneCodeModalVisible, setPhoneCodeModalVisible] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);
  const [verifyingPhoneCode, setVerifyingPhoneCode] = useState(false);
  const [startingIdentityVerification, setStartingIdentityVerification] =
    useState(false);
  const [startingVerificationPayment, setStartingVerificationPayment] =
    useState(false);
  const [refundPolicyAccepted, setRefundPolicyAccepted] = useState(false);
  const [requestingRefund, setRequestingRefund] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(
    null,
  );

  const storedVerifiedPhone =
    profileQuery.data?.verification?.phone?.phoneNumber ?? null;

  useEffect(() => {
    if (storedVerifiedPhone) {
      setPhoneNumber(formatUsPhoneNumberDisplay(storedVerifiedPhone));
    }
  }, [storedVerifiedPhone]);

  useEffect(() => {
    if (!params.verificationPayment && !params.identity) {
      return;
    }

    if (params.verificationPayment === 'success') {
      void handlePaymentReturnRefresh();
      return;
    }

    void handleRefreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.identity, params.verificationPayment]);

  if (profileQuery.isLoading) {
    return <LoadingView message="Loading verification status..." />;
  }

  const verification = parseVerifiedAccount(profileQuery.data?.verification);
  const currentStep = nextVerifiedAccountWizardStep({
    emailVerified: user?.emailVerified === true,
    verification,
  });
  const paymentComplete = verification?.payment.status === 'paid';
  const paymentRefundPending =
    verification?.payment.status === 'refund_pending';
  const paymentRefunded = verification?.payment.status === 'refunded';
  const contactComplete =
    user?.emailVerified === true && verification?.phone.status === 'verified';
  const identityComplete = verification?.identity.status === 'verified';
  const normalizedPhoneNumber = normalizeUsPhoneNumber(phoneNumber);
  const canRequestRefund =
    paymentComplete && verification?.identity.status === 'unverified';

  async function handleRefreshStatus({
    showToast = true,
  }: { showToast?: boolean } = {}) {
    setCheckingStatus(true);

    try {
      await refreshCurrentUser();
      await refreshVerifiedAccountStatus();
      await profileQuery.refetch();
      await queryClient.invalidateQueries({
        queryKey: publicProfileQueryKey(user?.uid),
      });

      if (showToast) {
        showSuccess(
          'Your verified account status is up to date.',
          'Status refreshed',
        );
      }
    } catch (caughtError) {
      showError(caughtError, 'Unable to refresh verification status.');
    } finally {
      setCheckingStatus(false);
    }
  }

  async function handlePaymentReturnRefresh() {
    setCheckingStatus(true);

    try {
      for (let attempt = 0; attempt < paymentReturnPollAttempts; attempt += 1) {
        await refreshCurrentUser();
        await refreshVerifiedAccountStatus();
        const refreshedProfile = await profileQuery.refetch();
        await queryClient.invalidateQueries({
          queryKey: publicProfileQueryKey(user?.uid),
        });

        if (
          parseVerifiedAccount(refreshedProfile.data?.verification).payment
            .status === 'paid'
        ) {
          showSuccess(
            'Your payment is complete. Continue with contact verification.',
            'Payment complete',
          );
          return;
        }

        if (attempt < paymentReturnPollAttempts - 1) {
          await wait(paymentReturnPollDelayMs);
        }
      }

      showSuccess(
        'Stripe confirmed your payment. If this step does not advance, tap Refresh status in a few seconds.',
        'Payment processing',
      );
    } catch (caughtError) {
      showError(caughtError, 'Unable to refresh verification status.');
    } finally {
      setCheckingStatus(false);
    }
  }

  async function handleStartPayment() {
    setStartingVerificationPayment(true);

    try {
      if (!paymentComplete && !refundPolicyAccepted) {
        throw new Error(
          'Review and accept the Refund Policy before starting payment.',
        );
      }

      if (Platform.OS !== 'web') {
        await startVerifiedAccountCheckout();
        showSuccess(
          'Frienzzi opened in your browser so you can pay the verification fee.',
          'Continue on web',
        );
        return;
      }

      if (paymentRefundPending) {
        showSuccess(
          'Your verification fee refund is still processing.',
          'Refund processing',
        );
        return;
      }

      const result = await createEmbeddedVerifiedAccountCheckoutSession();

      if (result.alreadyPaid) {
        await handleRefreshStatus({ showToast: false });
        showSuccess(
          'Your verification payment is already complete.',
          'Payment complete',
        );
        return;
      }

      if (!result.clientSecret) {
        throw new Error('Stripe did not return an embedded payment form.');
      }

      setPaymentClientSecret(result.clientSecret);
    } catch (caughtError) {
      showError(caughtError, 'Unable to start verification payment.');
    } finally {
      setStartingVerificationPayment(false);
    }
  }

  function handleClosePaymentModal() {
    setPaymentClientSecret(null);
  }

  function confirmRefundRequest() {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return window.confirm(
        'Request a refund for the verified account fee? This is only available before identity verification starts.',
      );
    }

    return new Promise<boolean>((resolve) => {
      Alert.alert(
        'Request refund?',
        'This is only available before identity verification starts.',
        [
          { onPress: () => resolve(false), style: 'cancel', text: 'Cancel' },
          {
            onPress: () => resolve(true),
            style: 'destructive',
            text: 'Request refund',
          },
        ],
      );
    });
  }

  async function handleRequestRefund() {
    const confirmed = await confirmRefundRequest();

    if (!confirmed) {
      return;
    }

    setRequestingRefund(true);

    try {
      await requestVerifiedAccountRefund();
      await handleRefreshStatus({ showToast: false });
      showSuccess(
        'Your refund request was sent to Stripe. We will update this page when Stripe confirms it.',
        'Refund requested',
      );
    } catch (caughtError) {
      showError(caughtError, 'Unable to request a verification refund.');
    } finally {
      setRequestingRefund(false);
    }
  }

  async function handleResendEmail() {
    setResendingEmail(true);

    try {
      await sendVerificationEmail('verifiedAccount');
      showSuccess(
        'Check your inbox for a new verification link.',
        'Email sent',
      );
    } catch (caughtError) {
      showError(caughtError, 'Unable to send verification email.');
    } finally {
      setResendingEmail(false);
    }
  }

  async function handleSendPhoneCode() {
    setSendingPhoneCode(true);

    try {
      const result = await sendPhoneVerificationCode(phoneNumber);

      if (result.handoff) {
        showSuccess(
          'Complete phone verification in the browser window that opened.',
          'Phone verification opened',
        );
        return;
      }

      setPhoneVerificationId(result.verificationId);
      setPhoneCodeModalVisible(true);
      showSuccess('Enter the SMS code sent to your phone.', 'Code sent');
    } catch (caughtError) {
      showError(caughtError, 'Unable to send phone verification code.');
    } finally {
      setSendingPhoneCode(false);
    }
  }

  function handlePhoneNumberChange(value: string) {
    setPhoneNumber(formatUsPhoneNumberInput(value));
    setPhoneVerificationCode('');
    setPhoneVerificationId(null);
    setPhoneCodeModalVisible(false);
  }

  async function handleConfirmPhoneCode() {
    if (!phoneVerificationId) {
      return;
    }

    setVerifyingPhoneCode(true);

    try {
      await confirmPhoneVerificationCode({
        code: phoneVerificationCode,
        verificationId: phoneVerificationId,
      });
      setPhoneVerificationCode('');
      setPhoneVerificationId(null);
      setPhoneCodeModalVisible(false);
      await handleRefreshStatus({ showToast: false });
      showSuccess('Your cell phone is verified.', 'Phone verified');
    } catch (caughtError) {
      showError(caughtError, 'Unable to verify that phone code.');
    } finally {
      setVerifyingPhoneCode(false);
    }
  }

  async function handleStartIdentityVerification() {
    setStartingIdentityVerification(true);

    try {
      const result = await startIdentityVerification();
      await profileQuery.refetch();

      if (!result.opened) {
        showSuccess(
          'Your identity check is already pending review. Refresh status in a few seconds.',
          'Identity review pending',
        );
        return;
      }

      if (Platform.OS === 'web') {
        return;
      }

      showSuccess(
        'Finish the Stripe Identity flow to complete ID verification.',
        'Identity verification opened',
      );
    } catch (caughtError) {
      showError(caughtError, 'Unable to start identity verification.');
    } finally {
      setStartingIdentityVerification(false);
    }
  }

  return (
    <Screen
      title="Get verified"
      subtitle="Complete three steps to qualify for a public Frienzzi checkmark."
    >
      <View style={styles.wizardShell}>
        <View style={styles.heroCard}>
          <View style={styles.heroBadge}>
            <ShieldCheck color={colors.surface} size={30} strokeWidth={2.4} />
          </View>
          <View style={styles.heroText}>
            <Text style={styles.heroTitle}>
              Verified accounts help keep Frienzzi real.
            </Text>
            <Text style={styles.description}>
              We check payment, contact ownership, and identity before showing
              the public badge. Normal app access does not require verification.
            </Text>
          </View>
        </View>

        <View style={styles.stepTracker}>
          {orderedSteps.map((step, index) => {
            const complete =
              step.key === 'payment'
                ? paymentComplete
                : step.key === 'contact'
                  ? contactComplete
                  : identityComplete;
            const active = currentStep === step.key;

            return (
              <View
                key={step.key}
                style={[
                  styles.trackerItem,
                  active ? styles.trackerItemActive : null,
                ]}
              >
                {complete ? (
                  <CheckCircle2
                    color={colors.success}
                    size={22}
                    strokeWidth={2.5}
                  />
                ) : (
                  <Circle
                    color={active ? colors.primary : colors.mutedText}
                    size={22}
                    strokeWidth={2.5}
                  />
                )}
                <View style={styles.trackerText}>
                  <Text style={styles.trackerTitle}>
                    {index + 1}. {step.title}
                  </Text>
                  <Text style={styles.trackerDescription}>
                    {statusLabel(complete, active)} - {step.description}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>

        <View style={styles.stepBody}>
          {currentStep === 'payment' ? (
            <View style={[styles.stepCard, styles.stepCardActive]}>
              <StepArt kind="payment" />
              <Text style={styles.stepEyebrow}>Step 1 of 3</Text>
              <View style={styles.stepHeader}>
                <View style={styles.inlineIcon}>
                  <CreditCard
                    color={colors.primary}
                    size={21}
                    strokeWidth={2.4}
                  />
                </View>
                <View style={styles.stepHeaderText}>
                  <Text style={styles.stepTitle}>Pay processing fee</Text>
                  <Text style={styles.description}>
                    This starts verification. The $10 fee plus applicable tax
                    covers processing costs and unlocks identity verification
                    attempts for this account. It can be refunded before
                    identity verification starts, for duplicate charges,
                    platform errors, or where required by law.
                  </Text>
                </View>
              </View>
              <View style={styles.infoBox}>
                <Text style={styles.infoTitle}>What happens in this step</Text>
                <Text style={styles.infoText}>
                  Stripe securely collects the payment and tax details. Frienzzi
                  stores only the payment status and Stripe IDs needed to
                  remember that this account already paid.
                </Text>
              </View>
              <View style={styles.checklistCard}>
                <ChecklistItem
                  complete={paymentComplete}
                  text="One successful payment unlocks future identity attempts."
                />
                <ChecklistItem text="Stripe Checkout handles the card form securely." />
                <ChecklistItem text="The fee is saved to this Frienzzi account." />
              </View>
              {!paymentComplete && !paymentRefundPending ? (
                <View style={styles.refundPolicyBox}>
                  <Text style={styles.infoTitle}>Refund policy</Text>
                  <Text style={styles.infoText}>
                    The verified account fee is $10 plus applicable tax. It
                    helps cover verification processing and does not guarantee
                    approval or a verified badge.
                  </Text>
                  <Text style={styles.infoText}>
                    You can request an automatic refund before Stripe Identity
                    starts. After identity verification starts, the fee is
                    generally non-refundable except for duplicate charges,
                    Frienzzi or provider errors, wrong-account operational
                    errors, or refunds required by law.
                  </Text>
                  <Link href="/refund-policy" style={styles.inlineLink}>
                    Read the full Refund Policy
                  </Link>
                  <Pressable
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: refundPolicyAccepted }}
                    onPress={() =>
                      setRefundPolicyAccepted((current) => !current)
                    }
                    style={({ pressed }) => [
                      styles.refundPolicyCheckbox,
                      refundPolicyAccepted
                        ? styles.refundPolicyCheckboxAccepted
                        : null,
                      pressed ? styles.refundPolicyCheckboxPressed : null,
                    ]}
                  >
                    {refundPolicyAccepted ? (
                      <CheckSquare2
                        color={colors.primary}
                        size={22}
                        strokeWidth={2.5}
                      />
                    ) : (
                      <Square
                        color={colors.secondary}
                        size={22}
                        strokeWidth={2.5}
                      />
                    )}
                    <Text style={styles.refundPolicyCheckboxText}>
                      I have read and understand the Refund Policy for the
                      verified account fee.
                    </Text>
                  </Pressable>
                </View>
              ) : null}
              {paymentRefundPending || paymentRefunded ? (
                <View style={styles.infoBox}>
                  <Text style={styles.infoTitle}>
                    {paymentRefundPending
                      ? 'Refund processing'
                      : 'Payment refunded'}
                  </Text>
                  <Text style={styles.infoText}>
                    {paymentRefundPending
                      ? 'Stripe is processing your refund. You can restart verification after the refund finishes.'
                      : 'Your previous verified account fee was refunded. You can pay again if you want to restart verification.'}
                  </Text>
                </View>
              ) : null}
              <View style={styles.stepActions}>
                <Button
                  title={
                    paymentRefundPending
                      ? 'Refund processing'
                      : paymentRefunded
                        ? 'Pay again'
                        : Platform.OS === 'web'
                          ? 'Open secure payment form'
                          : 'Pay on frienzzi.com'
                  }
                  loading={startingVerificationPayment}
                  disabled={
                    paymentRefundPending ||
                    startingVerificationPayment ||
                    (!paymentComplete && !refundPolicyAccepted)
                  }
                  onPress={handleStartPayment}
                />
              </View>
            </View>
          ) : null}

          {currentStep === 'contact' ? (
            <View style={[styles.stepCard, styles.stepCardActive]}>
              <StepArt kind="contact" />
              <Text style={styles.stepEyebrow}>Step 2 of 3</Text>
              <View style={styles.stepHeader}>
                <View style={styles.inlineIcon}>
                  <Mail color={colors.primary} size={21} strokeWidth={2.4} />
                </View>
                <View style={styles.stepHeaderText}>
                  <Text style={styles.stepTitle}>
                    Verify email and cell phone
                  </Text>
                  <Text style={styles.description}>
                    Email confirms you control the account inbox. Phone
                    verification makes automated and impersonation abuse harder
                    by linking a real cell number to your Firebase Auth account.
                  </Text>
                </View>
              </View>
              <View style={styles.infoBox}>
                <Text style={styles.infoTitle}>What happens in this step</Text>
                <Text style={styles.infoText}>
                  Your email and phone stay private. The public result is only
                  whether the verified badge can appear beside your name.
                </Text>
              </View>
              <View style={styles.statusPillRow}>
                <Text
                  style={[
                    styles.statusPill,
                    user?.emailVerified ? styles.statusPillComplete : null,
                  ]}
                >
                  Email: {user?.emailVerified ? 'Verified' : 'Not verified'}
                </Text>
                <Text
                  style={[
                    styles.statusPill,
                    verification?.phone.status === 'verified'
                      ? styles.statusPillComplete
                      : null,
                  ]}
                >
                  Phone:{' '}
                  {verification?.phone.status === 'verified'
                    ? `Verified ending in ${verification.phone.last4}`
                    : 'Not verified'}
                </Text>
              </View>
              <View style={styles.checklistCard}>
                <ChecklistItem
                  complete={user?.emailVerified === true}
                  text="Email proves you control this account inbox."
                />
                <ChecklistItem
                  complete={verification?.phone.status === 'verified'}
                  text="Cell phone verification helps reduce automated abuse."
                />
              </View>
              {user?.emailVerified ? null : (
                <View style={styles.actionGroup}>
                  <Text style={styles.description}>
                    Current email: {user?.email ?? 'Not available'}
                  </Text>
                  <View style={styles.buttonRow}>
                    <Button
                      title="Resend email"
                      variant="secondary"
                      loading={resendingEmail}
                      disabled={resendingEmail}
                      style={styles.rowButton}
                      onPress={handleResendEmail}
                    />
                    <Button
                      title="I verified it"
                      variant="secondary"
                      loading={checkingStatus}
                      disabled={checkingStatus}
                      style={styles.rowButton}
                      onPress={() => {
                        void handleRefreshStatus();
                      }}
                    />
                  </View>
                </View>
              )}
              <View style={[styles.actionGroup, styles.stepActions]}>
                <View style={styles.stepHeader}>
                  <View style={styles.inlineIconSmall}>
                    <Phone color={colors.primary} size={18} strokeWidth={2.4} />
                  </View>
                  <Text style={styles.subsectionTitle}>Cell phone</Text>
                </View>
                <Input
                  label="Phone number"
                  autoComplete="tel"
                  keyboardType="phone-pad"
                  onChangeText={handlePhoneNumberChange}
                  placeholder="(303) 555-0123"
                  textContentType="telephoneNumber"
                  value={phoneNumber}
                />
                <Text style={styles.fieldHelpText}>
                  US numbers are accepted with or without spaces, dashes,
                  parentheses, or +1.
                </Text>
                {Platform.OS === 'web' ? (
                  <View
                    nativeID="frienzzi-phone-verification-recaptcha"
                    style={styles.recaptchaContainer}
                  />
                ) : null}
                <Button
                  title={
                    Platform.OS === 'web'
                      ? 'Send phone verification code'
                      : 'Verify phone in browser'
                  }
                  loading={sendingPhoneCode}
                  disabled={sendingPhoneCode || !normalizedPhoneNumber}
                  onPress={handleSendPhoneCode}
                />
                {phoneVerificationId ? (
                  <Button
                    title="Enter verification code"
                    variant="secondary"
                    onPress={() => setPhoneCodeModalVisible(true)}
                  />
                ) : null}
              </View>
            </View>
          ) : null}

          {currentStep === 'identity' ? (
            <View style={[styles.stepCard, styles.stepCardActive]}>
              <StepArt kind="identity" />
              <Text style={styles.stepEyebrow}>Step 3 of 3</Text>
              <View style={styles.stepHeader}>
                <View style={styles.inlineIcon}>
                  <IdCard color={colors.primary} size={21} strokeWidth={2.4} />
                </View>
                <View style={styles.stepHeaderText}>
                  <Text style={styles.stepTitle}>Verify identity</Text>
                  <Text style={styles.description}>
                    Stripe Identity checks a government ID and selfie. Frienzzi
                    stores only minimal verification status and identifiers, not
                    ID images or document numbers. If approved, your profile
                    name is locked to the verified ID name.
                  </Text>
                </View>
              </View>
              <View style={styles.infoBox}>
                <Text style={styles.infoTitle}>What happens in this step</Text>
                <Text style={styles.infoText}>
                  Stripe checks the document and selfie. Frienzzi does not store
                  ID images, selfie images, document numbers, or raw Stripe
                  reports.
                </Text>
              </View>
              <View style={styles.checklistCard}>
                <ChecklistItem text="Government ID is checked by Stripe Identity." />
                <ChecklistItem text="Selfie match helps confirm the ID belongs to you." />
                <ChecklistItem text="Approved identity locks your public full name." />
              </View>
              <Text style={styles.statusLine}>
                Identity:{' '}
                {verification?.identity.status === 'pending'
                  ? 'Pending review'
                  : verification?.identity.status === 'requires_input'
                    ? 'Ready to continue'
                    : 'Not verified'}
              </Text>
              <View style={styles.stepActions}>
                <Button
                  title={
                    verification?.identity.status === 'requires_input'
                      ? 'Continue identity check'
                      : 'Start identity check'
                  }
                  loading={startingIdentityVerification}
                  disabled={startingIdentityVerification}
                  onPress={handleStartIdentityVerification}
                />
              </View>
            </View>
          ) : null}

          {currentStep === 'complete' ? (
            <View style={styles.completeCard}>
              <StepArt kind="complete" />
              <View style={styles.stepHeaderText}>
                <Text style={styles.stepTitle}>
                  {verification?.badge.verified
                    ? 'Your verified badge is active.'
                    : 'Your verification steps are complete.'}
                </Text>
                <Text style={styles.description}>
                  {verification?.badge.verified
                    ? 'The checkmark can now appear beside your name on Frienzzi profile and content surfaces.'
                    : 'Refresh status if the public checkmark does not appear within a moment.'}
                </Text>
              </View>
              <View style={styles.checklistCard}>
                <ChecklistItem complete text="Payment complete." />
                <ChecklistItem complete text="Email and phone verified." />
                <ChecklistItem
                  complete
                  text="Identity verification complete."
                />
              </View>
            </View>
          ) : null}
        </View>

        <View style={styles.footerActions}>
          {canRequestRefund ? (
            <View style={styles.refundBox}>
              <Text style={styles.infoTitle}>Need to stop verification?</Text>
              <Text style={styles.infoText}>
                You can request an automatic refund before starting Stripe
                Identity. After identity verification starts, refunds are
                limited to duplicate charges, platform errors, or legal
                requirements.
              </Text>
              <Button
                title="Request refund"
                variant="danger"
                loading={requestingRefund}
                disabled={requestingRefund}
                onPress={handleRequestRefund}
              />
            </View>
          ) : null}

          {currentStep === 'complete' && verification?.badge.verified ? null : (
            <Button
              title="Refresh status"
              variant="secondary"
              loading={checkingStatus}
              onPress={() => {
                void handleRefreshStatus();
              }}
            />
          )}

          <Button
            title="Back to profile"
            variant="secondary"
            onPress={() => router.push('/profile')}
          />
        </View>

        <Modal
          animationType="fade"
          transparent
          visible={Boolean(paymentClientSecret)}
          onRequestClose={handleClosePaymentModal}
        >
          <View style={styles.paymentModalRoot}>
            <Pressable
              accessibilityLabel="Close payment form"
              style={styles.paymentModalBackdrop}
              onPress={handleClosePaymentModal}
            />
            <View style={styles.paymentModalCard}>
              <View style={styles.paymentModalHeader}>
                <View style={styles.paymentModalTitleGroup}>
                  <Text style={styles.paymentModalEyebrow}>
                    Verified account
                  </Text>
                  <Text style={styles.paymentModalTitle}>
                    Pay processing fee
                  </Text>
                </View>
                <Pressable
                  accessibilityLabel="Close payment form"
                  hitSlop={10}
                  style={styles.paymentModalClose}
                  onPress={handleClosePaymentModal}
                >
                  <X color={colors.text} size={22} strokeWidth={2.5} />
                </Pressable>
              </View>
              <Text style={styles.paymentModalDescription}>
                Stripe securely handles the $10 plus applicable tax payment.
                Frienzzi stores only the payment status and Stripe IDs needed
                for your verification.
              </Text>
              <ScrollView
                contentContainerStyle={styles.paymentModalScrollContent}
                style={styles.paymentModalScroll}
              >
                <VerifiedAccountEmbeddedCheckout
                  clientSecret={paymentClientSecret}
                  onComplete={() => {
                    setPaymentClientSecret(null);
                    void handleRefreshStatus({ showToast: true });
                  }}
                />
              </ScrollView>
            </View>
          </View>
        </Modal>
        <PhoneVerificationCodeModal
          code={phoneVerificationCode}
          description="Enter the SMS code we sent to the phone number you entered."
          loading={verifyingPhoneCode}
          visible={Boolean(phoneVerificationId) && phoneCodeModalVisible}
          onChangeCode={setPhoneVerificationCode}
          onClose={() => setPhoneCodeModalVisible(false)}
          onConfirm={handleConfirmPhoneCode}
        />
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  actionGroup: {
    gap: spacing.md,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  checklistCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
  },
  checklistItem: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  checklistText: {
    color: colors.text,
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  completeCard: {
    backgroundColor: '#f1fbf6',
    borderColor: '#ccefdc',
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.lg,
  },
  description: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  fieldHelpText: {
    color: colors.mutedText,
    fontSize: 12,
    lineHeight: 17,
    marginTop: -spacing.xs,
  },
  heroCard: {
    alignItems: 'flex-start',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.lg,
  },
  heroBadge: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    height: 54,
    justifyContent: 'center',
    shadowColor: colors.primary,
    shadowOffset: { height: 8, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 14,
    width: 54,
  },
  heroText: {
    flex: 1,
    gap: spacing.xs,
  },
  heroTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
    lineHeight: 26,
  },
  footerActions: {
    borderTopColor: colors.border,
    borderTopWidth: 1,
    gap: spacing.md,
    paddingTop: spacing.lg,
  },
  infoBox: {
    backgroundColor: colors.accentSoft,
    borderColor: '#cbe9ff',
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xs,
    padding: spacing.md,
  },
  infoText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  infoTitle: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  inlineLink: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: '900',
    lineHeight: 20,
  },
  paymentModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(26, 12, 62, 0.52)',
  },
  paymentModalCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    boxShadow: '0 20px 60px rgba(26, 12, 62, 0.24)',
    gap: spacing.md,
    maxHeight: '92%',
    maxWidth: 720,
    padding: spacing.lg,
    width: '94%',
  },
  paymentModalClose: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  paymentModalDescription: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  paymentModalEyebrow: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  paymentModalHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  paymentModalRoot: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.md,
  },
  paymentModalScroll: {
    marginHorizontal: -spacing.sm,
  },
  paymentModalScrollContent: {
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.sm,
  },
  paymentModalTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
    lineHeight: 26,
  },
  paymentModalTitleGroup: {
    flex: 1,
    gap: spacing.xs,
  },
  recaptchaContainer: {
    alignSelf: 'flex-start',
    minHeight: 78,
    minWidth: 304,
  },
  refundPolicyBox: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
  },
  refundPolicyCheckbox: {
    alignItems: 'flex-start',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.sm,
  },
  refundPolicyCheckboxAccepted: {
    borderColor: colors.primary,
  },
  refundPolicyCheckboxPressed: {
    opacity: 0.78,
  },
  refundPolicyCheckboxText: {
    color: colors.text,
    flex: 1,
    fontSize: 13,
    fontWeight: '800',
    lineHeight: 19,
  },
  refundBox: {
    backgroundColor: '#fff7ed',
    borderColor: '#fed7aa',
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
  },
  rowButton: {
    flex: 1,
  },
  inlineIcon: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 42,
    justifyContent: 'center',
    width: 42,
  },
  inlineIconSmall: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  statusLine: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 20,
  },
  statusPill: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    color: colors.text,
    fontSize: 13,
    fontWeight: '800',
    overflow: 'hidden',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  statusPillComplete: {
    backgroundColor: '#f1fbf6',
    borderColor: '#ccefdc',
    color: colors.success,
  },
  statusPillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  stepActions: {
    gap: spacing.md,
  },
  stepBody: {
    gap: spacing.lg,
  },
  stepCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.lg,
    overflow: 'hidden',
    padding: spacing.lg,
  },
  stepCardActive: {
    borderColor: colors.primary,
    borderWidth: 2,
  },
  stepArt: {
    alignSelf: 'center',
    height: 138,
    marginBottom: spacing.xs,
    width: 188,
  },
  stepArtGlow: {
    backgroundColor: colors.accentSoft,
    borderRadius: 8,
    bottom: 12,
    left: 20,
    position: 'absolute',
    right: 20,
    top: 20,
  },
  stepArtOrb: {
    alignItems: 'center',
    borderRadius: 8,
    justifyContent: 'center',
    position: 'absolute',
  },
  stepArtOrbAccent: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderWidth: 1,
    bottom: 24,
    height: 54,
    right: 32,
    shadowColor: colors.secondary,
    shadowOffset: { height: 6, width: 0 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    width: 54,
  },
  stepArtOrbPrimary: {
    backgroundColor: colors.primary,
    height: 92,
    left: 38,
    shadowColor: colors.primary,
    shadowOffset: { height: 10, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    top: 16,
    width: 92,
  },
  stepArtSpark: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    position: 'absolute',
    right: 28,
    top: 8,
    width: 34,
  },
  stepEyebrow: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  stepHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
  },
  stepHeaderText: {
    flex: 1,
    gap: spacing.xs,
  },
  stepTitle: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
    lineHeight: 22,
  },
  stepTracker: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
  },
  subsectionTitle: {
    color: colors.text,
    flex: 1,
    fontSize: 15,
    fontWeight: '900',
  },
  trackerDescription: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16,
  },
  trackerItem: {
    alignItems: 'center',
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.md,
  },
  trackerItemActive: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.primary,
  },
  trackerText: {
    flex: 1,
    gap: spacing.xs,
  },
  trackerTitle: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  wizardShell: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.lg,
    padding: spacing.md,
  },
});
